


import { useState, FormEvent } from 'react';

interface ContactProps {
  showModal: boolean;
  onClose: () => void;
}

export default function Contact({ showModal, onClose }: ContactProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4egunvcmulmicuijfjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => {
          setSubmitStatus('idle');
          onClose();
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formBody = new URLSearchParams(new FormData(form) as any);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4egunvcmulmicuijfk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        form.reset();
        alert('Message sent successfully! We\'ll get back to you soon.');
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <>
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to give your vehicle the care it deserves? Contact us today to schedule your appointment.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-yellow-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-900">Phone</h4>
                    <a href="tel:+18692165666" className="text-gray-600 hover:text-green-700 transition-colors cursor-pointer">
                      +1 (869) 216-5666
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-yellow-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-900">Email</h4>
                    <a href="mailto:yaadboyzdetailing@gmail.com" className="text-gray-600 hover:text-green-700 transition-colors cursor-pointer">
                      yaadboyzdetailing@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-yellow-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-900">Location</h4>
                    <p className="text-gray-600">United States</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-yellow-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-900">Business Hours</h4>
                    <p className="text-gray-600">Booking required - message us for open times</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="font-bold text-lg mb-4 text-gray-900">Follow Us</h4>
                <div className="flex gap-4">
                  <a href="https://instagram.com/yaadboyzdetail" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-green-700 rounded-lg flex items-center justify-center transition-colors cursor-pointer group">
                    <i className="ri-instagram-line text-2xl text-gray-700 group-hover:text-yellow-400"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Send Us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form id="contact-form">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Name</label>
                  <input 
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Email</label>
                  <input 
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Phone</label>
                  <input 
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                    placeholder="+1 (876) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Service Interested In</label>
                  <input 
                    type="text"
                    name="service"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                    placeholder="e.g., Full Detail Package"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Message</label>
                  <textarea 
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your vehicle and what you need..."
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full bg-green-700 hover:bg-green-800 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full relative">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900">Book Your Appointment</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="booking-form">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Name</label>
                <input 
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Email</label>
                <input 
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Phone</label>
                <input 
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                  placeholder="+1 (876) 000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Service</label>
                <input 
                  type="text"
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({...formData, service: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all text-sm"
                  placeholder="Select a service"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Additional Notes</label>
                <textarea 
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-700 focus:ring-2 focus:ring-green-700/20 outline-none transition-all resize-none text-sm"
                  placeholder="Any special requests?"
                ></textarea>
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-100 text-green-800 p-4 rounded-lg text-center">
                  Booking submitted successfully! We'll contact you soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-100 text-red-800 p-4 rounded-lg text-center">
                  Something went wrong. Please try again.
                </div>
              )}
              
              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Booking'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

